import { completeMatterConnections } from "@/lib/data/requests/connections/owned-connections";
import { authenticatedFastApiAxiosInstance } from "@/lib/data/http";
import { VITE_API_STAGE } from "@/lib/data/static/importEnv";
import {
  type APIResponse,
  type Matter,
  type MatterV3,
  type SourceOwnedConnectionCreate,
} from "@/lib/types";

const API_STAGE = VITE_API_STAGE;

export const baseFastApiMatterRoute = "matters/v2";
export const baseMatterFilesRoute = `matter-file/${API_STAGE}/v1`;
export const baseFastApiMedicalTreatmentRoute = `${baseFastApiMatterRoute}/medical-treatments`;
export const baseReportsRoutes = `reports/${API_STAGE}/v1`;

export type RetrievedMatter = Matter & {
  assignees?: Array<{
    dateTimeEnd?: string | null;
    id: string;
    label?: string | null;
    user?: {
      firstName?: string | null;
      id: string;
      lastName?: string | null;
      profilePicturePresignedUrlKey?: string | null;
    } | null;
  }>;
  aiAcknowledgement?: boolean | null;
  aiAffectedFields?: unknown[] | null;
  aiStatus?: string | null;
  attorneyFee?: string | number | null;
  clientCommunication?: boolean | null;
  clientCommunicationDescription?: string | null;
  clientHealthInsurance?: boolean | null;
  clientInjuryDescription?: string | null;
  clientMaritalStatus?: string | null;
  clientMarried?: boolean | null;
  clientMinorAtIncident?: boolean | null;
  clientNoSocialSecurity?: boolean | null;
  clientPriorClaim?: boolean | null;
  clientPriorClaimDescription?: string | null;
  clientPriorInjury?: boolean | null;
  clientPriorInjuryDescription?: string | null;
  clientPriorInjuryLegalRepresentation?: boolean | null;
  clientRentalCar?: boolean | null;
  clientRentalCarDays?: number | null;
  clientSoughtTreatment?: boolean | null;
  clientVisibleBodilyInjury?: boolean | null;
  criticalInfo?: string | null;
  dateCreated: string;
  dateModified: string;
  dateOfLoss?: string | null;
  dateRetained?: string | null;
  deathOccurredFromIncident?: boolean | null;
  governmentEntityInvolved?: boolean | null;
  emailAddress?: string | null;
  filedDate?: string | null;
  firmId?: string | null;
  id: string;
  insurances?: unknown[];
  incidentAddressCity?: string | null;
  incidentAddressCountry?: string | null;
  incidentAddressCounty?: string | null;
  incidentAddressDetails?: string | null;
  incidentAddressState?: string | null;
  incidentAddressStreet1?: string | null;
  incidentAddressStreet2?: string | null;
  incidentAddressZip?: string | null;
  incidentDescription?: string | null;
  incidentDuringScopeOfEmployment?: boolean | null;
  incidentDuringScopeOfEmploymentTravelingFrom?: string | null;
  incidentDuringScopeOfEmploymentTravelingTo?: string | null;
  incidentInjuryDescription?: string | null;
  incidentPoliceReport?: boolean | null;
  incidentPoliceReportNumber?: string | null;
  incidentReportNotApplicable?: boolean | null;
  incidentRoadConditions?: string | null;
  incidentTime?: string | null;
  incidentWeatherConditions?: string | null;
  intakeDate?: string | null;
  lastSeenDatetime?: string | null;
  lastSeenUsername?: string | null;
  lastTouchDataBeforeAction?: string | null;
  lastTouchDataCurrent?: string | null;
  lastTouchDatetime?: string | null;
  lastTouchMessage?: string | null;
  lastTouchUsername?: string | null;
  leadSourceId?: string | null;
  massTortId?: string | null;
  notes?: Array<{
    content?: string | null;
    dateCreated?: string | null;
    id: string;
    message?: string | null;
    title?: string | null;
  }>;
  relatedMatterConnections?: Array<{
    id: string;
    matterId?: string | null;
    relatedMatterId?: string | null;
    relatedMatterTitle?: string | null;
    title?: string | null;
  }>;
  status?: { category?: string | null; id: string; label?: string | null };
  statusId?: string | null;
  statusPhases?: Array<{
    dateTimeEnd?: string | null;
    dateTimeStart?: string | null;
    id: string;
    status?: { category?: string | null; id: string; label?: string | null };
    statusId?: string | null;
  }>;
  statuteOfLimitations?: string | null;
  statuteOfLimitationsConfirmed?: boolean | null;
  summary?: string | null;
  tagConnections?: Array<{
    color?: string | null;
    id: string;
    label?: string | null;
    tag?: { color?: string | null; id: string; label?: string | null } | null;
  }>;
  tasks?: Array<{
    dateCreated?: string | null;
    dueDate?: string | null;
    id: string;
    status?: string | null;
    title?: string | null;
  }>;
  teamConnections?: Array<{
    dateTimeEnd?: string | null;
    id: string;
    label?: string | null;
    team?: { id: string; label?: string | null } | null;
  }>;
  title?: string | null;
  incidentType?: {
    incidentTypeCategoryId?: string | null;
    id: string;
    label?: string | null;
  };
  claimTypeIds?: string[];
  claimTypes?: Array<{ id: string; label: string }>;
  incidentTypeId?: string | null;
  eventLogs?: Array<{
    actor?: {
      firstName?: string | null;
      id: string;
      lastName?: string | null;
    } | null;
    actionType?: string | null;
    id: string;
    message?: string | null;
    occurredAt?: string | null;
    recordedAt?: string | null;
  }>;
};

export type CreateMatterContactConnectionInput = {
  contactId: string;
  dateTimeEnd?: string | null;
  dateTimeStart?: string | null;
  description?: string | null;
  expenseId?: string | null;
  faultStatus?: string | null;
  incidentReportId?: string | null;
  insuranceCoverage?: string | null;
  insuranceId?: string | null;
  label: string;
  lienId?: string | null;
  litigationDateId?: string | null;
  lostWagesId?: string | null;
  medicalTreatmentAppointmentId?: string | null;
  medicalTreatmentBalanceConfirmationId?: string | null;
  medicalTreatmentBillId?: string | null;
  medicalTreatmentId?: string | null;
  medicalTreatmentPlanId?: string | null;
  medicalTreatmentRecordId?: string | null;
  propertyDamageId?: string | null;
  settlementDemandId?: string | null;
  settlementOfferId?: string | null;
  shareFolderId?: string | null;
  shareFolderPasscode?: string | null;
  signatureRequestComplete?: boolean | null;
  signatureRequestId?: string | null;
  signatureRequestPasscode?: string | null;
  statuteOfLimitationsId?: string | null;
  trustCheckId?: string | null;
  vehicleId?: string | null;
  witnessStatus?: string | null;
};

export type CreateMatterInput = {
  attorneyFee: number | string | Record<string, unknown>;
  clientCommunication?: boolean | null;
  clientCommunicationDescription?: string | null;
  clientHealthInsurance?: boolean | null;
  clientInjuryDescription?: string | null;
  clientMaritalStatus?: string | null;
  clientMarried?: boolean | null;
  clientMinorAtIncident?: boolean | null;
  clientNoSocialSecurity?: boolean | null;
  clientPriorClaim?: boolean | null;
  clientPriorClaimDescription?: string | null;
  clientPriorInjury?: boolean | null;
  clientPriorInjuryDescription?: string | null;
  clientPriorInjuryLegalRepresentation?: boolean | null;
  clientRentalCar?: boolean | null;
  clientRentalCarDays?: number | null;
  clientSoughtTreatment?: boolean | null;
  clientVisibleBodilyInjury?: boolean | null;
  connections: SourceOwnedConnectionCreate[];
  criticalInfo?: string | null;
  dateOfLoss?: string | null;
  dateRetained?: string | null;
  deathOccurredFromIncident?: boolean | null;
  governmentEntityInvolved?: boolean | null;
  filedDate?: string | null;
  incidentAddressCity?: string | null;
  incidentAddressCountry: string;
  incidentAddressCounty?: string | null;
  incidentAddressDetails?: string | null;
  incidentAddressState: string;
  incidentAddressStreet1?: string | null;
  incidentAddressStreet2?: string | null;
  incidentAddressZip?: string | null;
  incidentDescription?: string | null;
  incidentDuringScopeOfEmployment?: boolean | null;
  incidentDuringScopeOfEmploymentTravelingFrom?: string | null;
  incidentDuringScopeOfEmploymentTravelingTo?: string | null;
  incidentInjuryDescription?: string | null;
  incidentPoliceReport?: boolean | null;
  incidentPoliceReportNumber?: string | null;
  incidentReportNotApplicable?: boolean | null;
  incidentRoadConditions?: string | null;
  incidentTime?: string | null;
  incidentWeatherConditions?: string | null;
  intakeDate?: string | null;
  leadSourceId?: string | null;
  massTortId?: string;
  statusId: string;
  statuteOfLimitations?: string | null;
  statuteOfLimitationsConfirmed?: boolean | null;
  summary?: string | null;
  teamId?: string | null;
  title: string;
  incidentTypeId: string;
  claimTypeIds?: string[];
};

export type UpdateMatterInput = Partial<
  Omit<CreateMatterInput, "connections" | "statusId">
> & {
  dateModified: string;
  id: string;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === "object" && !Array.isArray(value);

const normalizeBalanceConfirmation = (confirmation: unknown) => {
  if (!isRecord(confirmation)) return confirmation;

  return {
    ...confirmation,
    medicalTreatmentBalanceConfirmationId:
      confirmation.medicalTreatmentBalanceConfirmationId ?? confirmation.id,
    medicalTreatmentId:
      confirmation.medicalTreatmentId ?? confirmation.treatmentId ?? undefined,
  };
};

const normalizeMatterFile = (file: unknown) => {
  if (!isRecord(file)) return file;

  return {
    ...file,
    matterFileId: file.matterFileId ?? file.id,
    matterFileIdLocation: file.matterFileIdLocation ?? file.idLocation,
    matterFileIsFolder: file.matterFileIsFolder ?? file.isFolder,
    matterFileName: file.matterFileName ?? file.name,
    matterFilePresignedUrlKey:
      file.matterFilePresignedUrlKey ?? file.presignedUrlKey,
    matterFileTags: file.matterFileTags ?? file.tags,
  };
};

const normalizeMatterResponse = <T extends APIResponse<unknown>>(
  response: T,
): T => {
  const normalizeMatter = (matter: unknown) => {
    if (!isRecord(matter)) return matter;

    const userConnections = Array.isArray(matter.userConnections)
      ? matter.userConnections
      : [];
    const balanceConfirmations = Array.isArray(
      matter.medicalTreatmentBalanceConfirmations,
    )
      ? matter.medicalTreatmentBalanceConfirmations
      : Array.isArray(matter.balanceConfirmations)
        ? matter.balanceConfirmations
        : [];
    const assignees = Array.isArray(matter.matterAssignee)
      ? matter.matterAssignee
      : Array.isArray(matter.assignees)
        ? matter.assignees
        : [];
    const statutesOfLimitations = Array.isArray(matter.statutesOfLimitations)
      ? matter.statutesOfLimitations
      : Array.isArray(matter.matterStatuteOfLimitations)
        ? matter.matterStatuteOfLimitations
        : [];
    const files = Array.isArray(matter.files)
      ? matter.files
      : Array.isArray(matter.matterFile)
        ? matter.matterFile
        : [];
    const insurances = Array.isArray(matter.insurances)
      ? matter.insurances
      : Array.isArray(matter.matterInsurance)
        ? matter.matterInsurance
        : [];
    const vehicles = Array.isArray(matter.vehicles) ? matter.vehicles : [];
    const noteComments = Array.isArray(matter.noteComments)
      ? matter.noteComments
      : [];

    return {
      ...matter,
      matterAssignee: assignees,
      assignees,
      userConnections,
      medicalTreatmentBalanceConfirmations: balanceConfirmations.map(
        normalizeBalanceConfirmation,
      ),
      balanceConfirmations: balanceConfirmations.map(
        normalizeBalanceConfirmation,
      ),
      statutesOfLimitations: statutesOfLimitations,
      files: files.map(normalizeMatterFile),
      matterFile: files.map(normalizeMatterFile),
      insurances: insurances,
      vehicles,
      noteComments,
    };
  };

  return {
    ...response,
    data: Array.isArray(response.data)
      ? response.data.map(normalizeMatter)
      : normalizeMatter(response.data),
  };
};

export async function retrieveMatterV3(matterId: string) {
  const response = await authenticatedFastApiAxiosInstance.get<
    APIResponse<MatterV3>
  >(`matters/v4/${matterId}`, {
    params: { history: false, connectionLimit: 25 },
  });
  return normalizeMatterResponse({
    ...response.data,
    data: await completeMatterConnections(response.data.data, matterId),
  });
}

export function createMatterV3(data: CreateMatterInput) {
  return authenticatedFastApiAxiosInstance
    .post<APIResponse<MatterV3>>("matters/v4", data)
    .then((res) => normalizeMatterResponse(res.data));
}

export function updateMatter(data: UpdateMatterInput) {
  return authenticatedFastApiAxiosInstance
    .patch<APIResponse<RetrievedMatter>>(baseFastApiMatterRoute, data)
    .then((res) => normalizeMatterResponse(res.data));
}

export function updateMatterIncidentReportNotApplicableV2(data: {
  dateModified: string;
  incidentReportNotApplicable: boolean;
  matterId: string;
}) {
  const { matterId, ...payload } = data;
  return authenticatedFastApiAxiosInstance
    .patch<
      APIResponse<RetrievedMatter>
    >(baseFastApiMatterRoute, { ...payload, id: matterId })
    .then((res) => normalizeMatterResponse(res.data));
}

export function deleteMatter(data: { id: string }) {
  return authenticatedFastApiAxiosInstance
    .delete<
      APIResponse<RetrievedMatter>
    >(`${baseFastApiMatterRoute}/${data.id}`)
    .then((res) => normalizeMatterResponse(res.data));
}
